import { ApiError } from '@fc/core'
import { Badge, Button, Card, CardContent, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, MapPin, Plus, Star, Trash2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createSite,
  deleteSite,
  listSites,
  updateSite,
  type NewSiteInput,
  type SiteDto,
} from '../../lib/onboarding-api'

const fieldClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

export function SitesAdminPage() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sites'],
    queryFn: listSites,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sites'] })

  const makePrimary = useMutation({
    mutationFn: (id: string) => updateSite(id, { isPrimary: true }),
    onSuccess: invalidate,
  })
  const removeSite = useMutation({
    mutationFn: (id: string) => deleteSite(id),
    onSuccess: invalidate,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sites & campuses"
        description="The physical locations where your ministry happens."
        actions={
          <>
            <Button asChild variant="ghost" size="sm">
              <Link to="/app/settings">
                <ArrowLeft />
                Settings
              </Link>
            </Button>
            <Button onClick={() => setCreating((v) => !v)}>
              <Plus />
              New site
            </Button>
          </>
        }
      />

      {creating && (
        <SiteForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            invalidate()
          }}
        />
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading sites…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn't load sites.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'You may not have permission.'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted">
            No sites yet. Add your first campus to get started.
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {data.map((site) => (
              <li key={site.id} className="flex items-center gap-4 px-5 py-4">
                <div className="grid size-9 shrink-0 place-items-center rounded-md bg-card-hover text-secondary">
                  <MapPin className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{site.name}</span>
                    {site.isPrimary && (
                      <Badge variant="primary" className="gap-1">
                        <Star className="size-3" /> Primary
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-xs text-dim">
                    {site.slug}
                    {site.timezone ? ` · ${site.timezone}` : ''}
                    {site.phone ? ` · ${site.phone}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {!site.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => makePrimary.mutate(site.id)}
                      disabled={makePrimary.isPending}
                    >
                      <Star className="size-4" />
                      Make primary
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${site.name}`}
                    disabled={site.isPrimary || removeSite.isPending}
                    onClick={() => {
                      if (confirm(`Delete "${site.name}"? This cannot be undone.`))
                        removeSite.mutate(site.id)
                    }}
                  >
                    <Trash2 className="size-4 text-danger" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {(makePrimary.isError || removeSite.isError) && (
        <p className="text-sm text-danger" role="alert">
          {(() => {
            const e = makePrimary.error ?? removeSite.error
            return e instanceof Error ? e.message : 'Action failed.'
          })()}
        </p>
      )}
    </div>
  )
}

function SiteForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [timezone, setTimezone] = useState('')
  const [phone, setPhone] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)

  const mutation = useMutation({
    mutationFn: (input: NewSiteInput) => createSite(input),
    onSuccess: onCreated,
  })

  const slugError =
    mutation.isError && mutation.error instanceof ApiError ? mutation.error.fields?.slug : undefined

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    mutation.mutate({
      name: name.trim(),
      slug: slug.trim() || name.trim(),
      timezone: timezone.trim() || null,
      phone: phone.trim() || null,
      isPrimary,
    })
  }

  return (
    <Card className="max-w-2xl">
      <CardContent className="pt-5">
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="site-name">Name</Label>
              <Input
                id="site-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Downtown Campus"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="site-slug">Slug</Label>
              <Input
                id="site-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="downtown"
                aria-invalid={Boolean(slugError) || undefined}
              />
              {slugError && (
                <p className="text-xs text-danger" role="alert">
                  {slugError}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="site-tz">Timezone</Label>
              <Input
                id="site-tz"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="America/Chicago"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="site-phone">Phone</Label>
              <Input
                id="site-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="size-4 rounded border-border"
            />
            Set as primary campus
          </label>

          {mutation.isError && !slugError && (
            <p className="text-sm text-danger" role="alert">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Could not create the site.'}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending ? <Spinner /> : null}
              Create site
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
